import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ApiClient } from '../api/client';
import type { AppItem, AppStageSecret, AppStageSecretsResponse, StageListResponse } from '../types';

function managedReason(secret: AppStageSecret): string | null {
  if (secret.source_type === 'external') return 'Update the source secret, then apply and deploy its consumers.';
  if (/^(identity|google)\./i.test(secret.key)) return 'Mission Control provisions this credential. Use its managed connection lifecycle.';
  if (/ENCRYPTION|SIGNING|TOKEN_KEY|SESSION_SECRET|JWT_SECRET|SSH_PRIVATE_KEY|^(POSTGRES|DATABASE)_PASSWORD$/i.test(secret.key)) return 'This key protects stored data or service identity. Rotation needs a migration or coordinated service update.';
  return null;
}

export default function ConnectionSecrets({ apiClient, apps }: { apiClient: ApiClient; apps: AppItem[] }) {
  const [appId, setAppId] = useState('');
  const [busy, setBusy] = useState(false);
  return <section className="card connection-secrets">
    <header className="connection-section-heading">
      <div><h3>Application secrets</h3><p>Choose an application and scope to replace a stored credential.</p></div>
      <span className="connection-status">Values hidden</span>
    </header>
    <div className="connection-field">
    <label htmlFor="connection-secret-app">Application</label>
    <select id="connection-secret-app" value={appId} disabled={busy} onChange={event => setAppId(event.target.value)}>
      <option value="">Choose an application</option>
      {apps.map(app => <option key={app.id} value={app.id}>{app.name} ({app.slug})</option>)}
    </select>
    </div>
    {!appId && <p className="connection-empty">Select an application to view its secret metadata and available scopes.</p>}
    {appId && <SecretScopes key={appId} apiClient={apiClient} appId={appId} busy={busy} onBusy={setBusy} />}
  </section>;
}

function SecretScopes({ apiClient, appId, busy, onBusy }: { apiClient: ApiClient; appId: string; busy: boolean; onBusy: (value: boolean) => void }) {
  const [stages, setStages] = useState<StageListResponse | null>(null);
  const [stageId, setStageId] = useState('');
  const [error, setError] = useState('');
  useEffect(() => {
    let active = true;
    void apiClient.get<StageListResponse>(`/api/apps/${encodeURIComponent(appId)}/stages`)
      .then(value => { if (active) setStages(value); })
      .catch(() => { if (active) setError('Could not load application scopes. Reload to try again.'); });
    return () => { active = false; };
  }, [apiClient, appId]);
  return <>
    {error && <p className="connection-notice connection-notice--error" role="alert">{error}</p>}
    {!stages && !error && <p className="connection-help" role="status">Loading scopes…</p>}
    {stages && <>
      <div className="connection-field">
      <label htmlFor="connection-secret-scope">Secret scope</label>
      <select id="connection-secret-scope" aria-describedby="connection-scope-help" value={stageId} disabled={busy} onChange={event => setStageId(event.target.value)}>
        <option value="">General (shared by this app’s stages)</option>
        {stages.stages.map(stage => <option key={stage.id} value={stage.id}>{stage.name}</option>)}
      </select>
      <p id="connection-scope-help" className="connection-help">Stage values override General values. Stages without an override may still inherit a General secret.</p>
      </div>
      <SecretValues key={stageId} apiClient={apiClient} appId={appId} stageId={stageId} onBusy={onBusy} />
    </>}
    <div className="connection-footer"><Link className="connection-text-link" to={`/apps/${encodeURIComponent(appId)}`}>Open application settings and deployment <span aria-hidden="true">→</span></Link></div>
  </>;
}

function SecretValues({ apiClient, appId, stageId, onBusy }: { apiClient: ApiClient; appId: string; stageId: string; onBusy: (value: boolean) => void }) {
  const [secrets, setSecrets] = useState<AppStageSecret[] | null>(null);
  const [selectedId, setSelectedId] = useState('');
  const [replacement, setReplacement] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState('');
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const selected = secrets?.find(secret => secret.id === selectedId);
  const reason = selected ? managedReason(selected) : null;
  useEffect(() => {
    let active = true;
    const query = stageId ? `?stage_id=${encodeURIComponent(stageId)}` : '';
    void apiClient.get<AppStageSecretsResponse>(`/api/apps/${encodeURIComponent(appId)}/secrets${query}`)
      .then(value => { if (active) setSecrets(value.secrets); })
      .catch(() => { if (active) setError('Could not load secrets. Reload to try again.'); });
    return () => { active = false; };
  }, [apiClient, appId, stageId]);

  async function save() {
    if (inFlight.current || !selected || reason || !replacement || !acknowledged) return;
    inFlight.current = true; setBusy(true); onBusy(true); setError(''); setResult('');
    try {
      const response = await apiClient.put<{ secret: AppStageSecret }>(`/api/apps/${encodeURIComponent(appId)}/secrets/${encodeURIComponent(selected.id)}`, { value: replacement });
      setSecrets(values => values?.map(value => value.id === response.secret.id ? response.secret : value) ?? []);
      setResult('Stored. Apply the deployment files and deploy each affected stage to activate this value. Then verify access before retiring the old credential at its provider.');
    } catch {
      setError('The update could not be confirmed. Reload the secret metadata before trying again.');
    } finally { setReplacement(''); setAcknowledged(false); inFlight.current = false; setBusy(false); onBusy(false); }
  }

  return <>
    {!secrets && !error && <p className="connection-help" role="status">Loading secret metadata…</p>}
    {secrets?.length === 0 && <p className="connection-empty">No secrets are stored directly in this scope. Use application settings to add a secret or override.</p>}
    {Boolean(secrets?.length) && <div className="connection-field"><label htmlFor="connection-secret-key">Secret</label><select id="connection-secret-key" value={selectedId} disabled={busy} onChange={event => { setSelectedId(event.target.value); setReplacement(''); setAcknowledged(false); setError(''); setResult(''); }}>
      <option value="">Choose a secret</option>
      {secrets?.map(secret => <option key={secret.id} value={secret.id}>{secret.key} — {secret.has_value ? 'Set' : 'Missing'}</option>)}
    </select></div>}
    {selected && <div className="connection-secret-editor">
      <div className="connection-secret-meta"><strong>{selected.key}</strong><span className="connection-help">Last updated: {new Date(selected.updated_at).toLocaleString()}</span></div>
      {reason ? <p className="connection-notice">{reason} {selected.external_source && <Link className="connection-text-link" to={`/apps/${encodeURIComponent(selected.external_source.app_id)}`}>Open source application</Link>}</p> : <>
        <p className="connection-help">Create the replacement credential with its provider first. Saving here updates only the stored deployment value.</p>
        <label className="connection-field">Replacement value {selected.secret_type === 'file'
          ? <textarea value={replacement} disabled={busy} autoComplete="off" spellCheck={false} onChange={event => setReplacement(event.target.value)} />
          : <input type="password" value={replacement} disabled={busy} autoComplete="new-password" onChange={event => setReplacement(event.target.value)} />}</label>
        <label className="connection-settings-consent"><input type="checkbox" checked={acknowledged} disabled={busy} onChange={event => setAcknowledged(event.target.checked)} /><span>I will apply and deploy the affected stages before retiring the previous credential.</span></label>
        <button type="button" disabled={busy || !replacement || !acknowledged} onClick={() => { void save(); }}>{busy ? 'Saving…' : 'Replace stored value'}</button>
      </>}
    </div>}
    {result && <p className="connection-notice connection-notice--success" role="status">{result}</p>}
    {error && <p className="connection-notice connection-notice--error" role="alert">{error}</p>}
  </>;
}
